import { useSession } from '@/hooks/useSession';
import { useSettings } from '@/hooks/useSettings';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { TranslationLanguageToggle } from '@/components/reader/TranslationLanguageToggle';
import { TafsirLanguageToggle } from '@/components/reader/TafsirLanguageToggle';
import { cn } from '@/lib/utils/cn';

const THEMES = [
  { v: 'light' as const, label: 'Light' },
  { v: 'dark' as const, label: 'Dark' },
  { v: 'system' as const, label: 'System' },
];

export function Settings() {
  useDocumentTitle('Settings');
  const session = useSession();
  const { settings, update, status } = useSettings();

  return (
    <section className="container-prose pt-12 sm:pt-16 pb-16">
      <header className="mb-10">
        <p className="text-xs uppercase tracking-[0.22em] text-accent-600 dark:text-accent-400 mb-3">
          Preferences
        </p>
        <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50">
          Settings
        </h1>
        <p className="text-sm text-ink-500 dark:text-ink-400 mt-2">
          Signed in as{' '}
          <span className="text-ink-700 dark:text-ink-200">
            {session.data?.user.email ?? '…'}
          </span>
          {status === 'syncing' && (
            <span className="ml-2 text-ink-400">syncing…</span>
          )}
        </p>
      </header>

      <div className="space-y-10">
        <Row label="Translation" description="Which translation columns to render under each ayah.">
          <TranslationLanguageToggle
            value={settings.translation_language}
            onChange={(v) => update({ translation_language: v })}
          />
        </Row>

        <Row label="Transliteration" description="Roman-script transliteration of the Arabic text.">
          <SwitchPill
            value={settings.show_transliteration}
            onChange={(v) => update({ show_transliteration: v })}
            label={settings.show_transliteration ? 'On' : 'Off'}
          />
        </Row>

        <Row label="Tafsir" description="Show inline tafsir under each ayah.">
          <div className="flex items-center gap-2">
            <SwitchPill
              value={settings.show_tafsir}
              onChange={(v) => update({ show_tafsir: v })}
              label={settings.show_tafsir ? 'On' : 'Off'}
            />
            {settings.show_tafsir && (
              <TafsirLanguageToggle
                value={settings.tafsir_language}
                onChange={(v) => update({ tafsir_language: v })}
              />
            )}
          </div>
        </Row>

        <Row label="Theme" description="Color mode. System follows your device.">
          <div
            role="radiogroup"
            aria-label="Theme"
            className="inline-flex p-1 rounded-full bg-ink-100 dark:bg-ink-900/70 border border-ink-200/70 dark:border-ink-800/70"
          >
            {THEMES.map((opt) => (
              <button
                key={opt.v}
                role="radio"
                aria-checked={settings.theme === opt.v}
                onClick={() => update({ theme: opt.v })}
                className={cn(
                  'px-4 h-8 text-sm rounded-full transition-colors duration-200',
                  settings.theme === opt.v
                    ? 'bg-white dark:bg-ink-700 text-ink-900 dark:text-ink-50 shadow-sm'
                    : 'text-ink-500 dark:text-ink-400 hover:text-ink-800 dark:hover:text-ink-100',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Row>

        <Row
          label="Audio"
          description="Reciter selection lands once Phase 2 audio data is seeded."
        >
          <span className="text-sm text-ink-400 dark:text-ink-500 italic">
            Coming with audio
          </span>
        </Row>
      </div>
    </section>
  );
}

function Row({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid sm:grid-cols-[1fr,auto] gap-x-8 gap-y-3 items-start">
      <div>
        <h2 className="font-serif text-lg text-ink-900 dark:text-ink-50">
          {label}
        </h2>
        {description && (
          <p className="text-sm text-ink-500 dark:text-ink-400 mt-1 leading-relaxed">
            {description}
          </p>
        )}
      </div>
      <div className="sm:pt-1">{children}</div>
    </div>
  );
}

function SwitchPill({
  value,
  onChange,
  label,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      className={cn(
        'inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-medium border',
        'transition-colors duration-200 ease-smooth',
        value
          ? 'bg-accent/10 border-accent/30 text-accent-600 dark:text-accent-400'
          : 'bg-ink-100/60 dark:bg-ink-900/70 border-ink-200/70 dark:border-ink-800/70 text-ink-500 dark:text-ink-400',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          value ? 'bg-accent' : 'bg-ink-400',
        )}
      />
      {label}
    </button>
  );
}
